import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Bell, Check, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 30_000;

const relativeTime = (iso) => {
  if (!iso) return '';
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
};

const Avatar = ({ actor, size = 32 }) => {
  const initial = (actor?.first_name || '?')[0]?.toUpperCase() || '?';
  if (actor?.profile_photo_url) {
    return (
      <img
        src={actor.profile_photo_url}
        alt=""
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-[#2CA8A0] text-black font-semibold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size / 2.5 }}
    >
      {initial}
    </div>
  );
};

const NotificationBell = ({ collapsed = false, variant = 'sidebar' }) => {
  const isHeader = variant === 'header';
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pulse, setPulse] = useState(false);
  const prevUnreadRef = useRef(0);
  const openRef = useRef(false);
  const panelRef = useRef(null);

  // Keep an always-fresh ref for `open` so fetchCount can read it without re-creating
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchCount = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/notifications/unread-count`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (!r.ok) return;
      const d = await r.json();
      const next = d.count || 0;
      // Trigger a short pulse if count went up and the panel is closed
      if (next > prevUnreadRef.current && !openRef.current) {
        setPulse(true);
        setTimeout(() => setPulse(false), 1800);
      }
      prevUnreadRef.current = next;
      setUnread(next);
    } catch (_) {}
  }, [authHeaders]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/notifications`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (!r.ok) return;
      const d = await r.json();
      setItems(d.notifications || []);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  // Poll unread count every 30s
  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, [fetchCount]);

  // Click outside closes
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  const handleRowClick = async (n) => {
    // Optimistic: mark read locally + decrement badge
    if (!n.read) {
      setItems((prev) =>
        (prev || []).map((x) => (x.notification_id === n.notification_id ? { ...x, read: true } : x))
      );
      setUnread((u) => Math.max(0, u - 1));
      fetch(`${API_URL}/api/notifications/${n.notification_id}/read`, {
        method: 'PATCH',
        headers: authHeaders(),
        credentials: 'include',
      }).catch(() => {});
    }
    setOpen(false);
    if (n.post_id) {
      navigate(`/portal/community#post-${n.post_id}`);
    }
  };

  const markAllRead = async () => {
    setItems((prev) => (prev || []).map((x) => ({ ...x, read: true })));
    setUnread(0);
    await fetch(`${API_URL}/api/notifications/read-all`, {
      method: 'PATCH',
      headers: authHeaders(),
      credentials: 'include',
    }).catch(() => {});
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={toggleOpen}
        className={
          isHeader
            ? `relative inline-flex items-center justify-center min-w-[44px] min-h-[44px] transition ${
                open ? 'text-[#2CA8A0]' : 'text-[#888] hover:text-white'
              }`
            : `relative w-full flex items-center gap-3 px-3 py-2.5 text-sm transition min-h-[44px] ${
                open
                  ? 'bg-[#2CA8A0]/10 text-[#2CA8A0]'
                  : 'text-[#888] hover:text-white hover:bg-[#FFFFFF]'
              }`
        }
        data-testid="notifications-bell"
        aria-label="Notifications"
      >
        <span className="relative inline-flex">
          <Bell
            className={`${isHeader ? 'w-5 h-5' : 'w-4 h-4'} ${pulse ? 'frg-bell-pulse' : ''}`}
            data-pulsing={pulse ? 'true' : 'false'}
          />
          {unread > 0 && (
            <span
              className={`absolute -top-1.5 -right-2 bg-red-500 text-white text-[10px] min-w-[16px] h-4 rounded-full flex items-center justify-center font-semibold px-1 ${
                pulse ? 'frg-bell-pulse' : ''
              }`}
              data-testid="notifications-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </span>
        {!collapsed && !isHeader && <span>Notifications</span>}
      </button>

      {open && (
        <div
          className={
            isHeader
              ? 'fixed right-2 top-14 z-50 w-[min(90vw,20rem)] bg-[#F9F6EF] border border-[#222] shadow-2xl'
              : 'absolute left-full ml-2 bottom-0 z-50 w-80 bg-[#F9F6EF] border border-[#222] shadow-2xl'
          }
          data-testid="notifications-panel"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-3 border-b border-[#FFFFFF]">
            <h3 className="text-white text-sm font-medium">Notifications</h3>
            <button
              onClick={markAllRead}
              className="text-[#2CA8A0] text-xs hover:underline"
              data-testid="mark-all-read-btn"
            >
              Mark all as read
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center p-6">
                <Loader2 className="w-4 h-4 text-[#2CA8A0] animate-spin" />
              </div>
            )}
            {!loading && items && items.length === 0 && (
              <div className="p-6 text-center text-[#666] text-sm">No notifications yet</div>
            )}
            {!loading && items && items.length > 0 && (
              <ul>
                {items.map((n) => (
                  <li key={n.notification_id}>
                    <button
                      onClick={() => handleRowClick(n)}
                      className={`w-full text-left flex items-start gap-3 p-3 border-b border-[#FFFFFF] transition ${
                        n.read
                          ? 'bg-transparent hover:bg-[#FFFFFF]'
                          : 'bg-[#1e3a52] hover:bg-[#244a68]'
                      }`}
                      data-testid={`notification-row-${n.notification_id}`}
                      data-read={n.read ? 'true' : 'false'}
                    >
                      <Avatar actor={n.actor} size={32} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${n.read ? 'text-[#aaa]' : 'text-white'}`}>
                          {n.message}
                        </p>
                        <p className="text-[#666] text-xs mt-0.5">
                          {relativeTime(n.created_at)}
                        </p>
                      </div>
                      {!n.read && (
                        <span className="w-2 h-2 rounded-full bg-[#2CA8A0] mt-1.5 shrink-0" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
